import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AuthFormRouter from "./AuthFormRouter";
import LanguageToggle from "./Shared/LanguageToggle";
import { AuthProvider } from "./AuthContext";
import cognitoAuthService from "../../services/cognitoAuthService";
import { LOGO_URL } from "../../constants";
import "../../styles/auth.css";

const isValidRedirectUrl = (url) => {
  try {
    const parsed = new URL(url);
    if (parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1") {
      return true;
    }
    const allowedDomain = process.env.REACT_APP_ALLOWED_REDIRECT_DOMAIN || "decisionvue.net";
    if (
      allowedDomain &&
      (parsed.hostname === allowedDomain ||
        parsed.hostname.endsWith(`.${allowedDomain}`))
    ) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

const STORAGE_KEY_REDIRECT = "auth_redirect";
const STORAGE_KEY_APP_ID = "auth_app_id";

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentView, setCurrentView] = useState("login");
  const [showAppLoading, setShowAppLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  // Derive appId and redirect from URL params first (source of truth for
  // the current visit), falling back to localStorage for continuity across
  // IdP callbacks and direct visits where the URL lacks these params.
  const [appId] = useState(
    () => searchParams.get("appId") || localStorage.getItem(STORAGE_KEY_APP_ID) || ""
  );
  const [externalRedirect] = useState(
    () => searchParams.get("redirect") || localStorage.getItem(STORAGE_KEY_REDIRECT) || ""
  );

  // Sync state-derived values to localStorage so they survive IdP redirects
  // and page reloads.
  useEffect(() => {
    if (appId) localStorage.setItem(STORAGE_KEY_APP_ID, appId);
    if (externalRedirect) localStorage.setItem(STORAGE_KEY_REDIRECT, externalRedirect);
  }, [appId, externalRedirect]);

  const initializeAppRef = useRef(null);

  const initializeApp = useCallback(async () => {
    setShowAppLoading(true);

    if (externalRedirect && isValidRedirectUrl(externalRedirect)) {
      window.location.href = externalRedirect;
      return;
    }

    // navigate("/dashboard");
  }, [externalRedirect]);

  // Keep ref in sync so stale closures in useEffect can call the latest version
  initializeAppRef.current = initializeApp;

  const navigateAfterAuth = useCallback(() => {
    initializeAppRef.current();
  }, []);

  useEffect(() => {
    const error = searchParams.get("error");
    if (error === "app_access_denied") {
      setAccessDenied(true);
      return;
    }

    // Check if already authenticated and entitled to target app
    (async () => {
      try {
        const result = await cognitoAuthService.isAuthenticated(appId);
        if (result.authenticated) {
          if (appId && result.entitled === false) {
            setAccessDenied(true);
          } else {
            await initializeAppRef.current();
          }
        }
      } catch (err) {
        console.error("Auth check failed:", err);
      }
    })();
  }, [appId, searchParams]);

  if (accessDenied) {
    return (
      <div className="login-page">
        <div className="access-denied-page" style={{ position: "relative" }}>
          <LanguageToggle />
          <h2>{t('accessDenied.title')}</h2>
          <p>{t('accessDenied.message')}</p>
          <p>{t('accessDenied.contactAdmin')}</p>
          <button
            className="btn btn-primary"
            style={{ width: "auto", padding: "12px 32px" }}
            onClick={() => {
              setAccessDenied(false);
              navigate("/login", { replace: true });
            }}
          >
            {t('accessDenied.backToSignIn')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <AuthProvider
      currentView={currentView}
      setCurrentView={setCurrentView}
      navigateAfterAuth={navigateAfterAuth}
      externalRedirect={externalRedirect}
      appId={appId}
    >
      <div className="login-page">
        <div className="brand-header">
          <img src={LOGO_URL} alt="Logo" className="brand-logo" />
        </div>
        <div className="login-card">
          <LanguageToggle />
          <AuthFormRouter showAppLoading={showAppLoading} />
        </div>
      </div>
    </AuthProvider>
  );
};

export default Login;
